import i18next from 'i18next'

import type {FetchOptions} from '../../@types/fetch'
import type {HydraError} from '../../@types/hydra/HydraError'
import type {HydraResponse} from '../../@types/hydra/HydraResponse'
import {API_ENDPOINT, APP_VERSION} from '../config'

import {AbortError, ApiError, NetworkError, TimeoutError, ViolationsError} from './ApiError'
import {reportInvalidation} from './invalidation'

const MIME_TYPE = 'application/ld+json'
const MIME_TYPE_PATH = 'application/merge-patch+json'

const multiSignals = (signals: AbortSignal[]) => {
  const controller = new AbortController()

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort()
      break
    }
    signal.addEventListener('abort', () => controller.abort(), {signal: controller.signal})
  }

  return controller.signal
}

export async function fetchApi(resource: string, init?: FetchOptions): Promise<Response> {
  // No default `credentials`: public reads stay uncredentialed so the HTTP caches are not keyed on cookies.
  const initParameters: FetchOptions = {
    ...init,
    headers: {
      Accept: MIME_TYPE,
      'Accept-Language': i18next.resolvedLanguage ?? 'en',
      'X-Topo-Version': APP_VERSION,
      ...init?.headers,
    },
  }

  const timeoutSignal = AbortSignal.timeout(20_000)
  if (initParameters.signal) {
    initParameters.signal = multiSignals([initParameters.signal, timeoutSignal])
  } else {
    initParameters.signal = timeoutSignal
  }

  let response: Response
  try {
    response = await fetch(`${API_ENDPOINT}${resource}`, initParameters)
  } catch (e) {
    if (timeoutSignal.aborted) {
      throw new TimeoutError()
    }

    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new AbortError()
    }

    if (e instanceof TypeError) {
      throw new NetworkError()
    }

    throw e
  }

  reportInvalidation(response.headers.get('xkey-invalidate'))

  return response
}

export async function fetchApiResource<T extends HydraResponse>(resource: string, init?: FetchOptions): Promise<T | null> {
  if (init) {
    if (init.body !== undefined && !(init.body instanceof FormData) && !init.headers?.['Content-Type']) {
      if (init.method === 'PATCH') {
        init.headers = {...init.headers, 'Content-Type': MIME_TYPE_PATH}
      } else {
        init.headers = {...init.headers, 'Content-Type': MIME_TYPE}
      }
    }
  }

  const resp = await fetchApi(resource, init)
  if (resp.status === 204 || resp.status === 202) {
    return null
  }

  if (resp.ok) {
    const json: T = await resp.json()

    if (json['@context']) {
      json['@tags'] = []
      if (resp.headers.has('xkey')) {
        const xkey = resp.headers.get('xkey')
        if (xkey) {
          json['@tags'] = xkey.split(/,/)
        }
      }
      if (json['@type'] === 'Collection') {
        json['@tags'].push(json['@context'])
      }
    }

    return json
  }

  const json: HydraError = await resp.json()

  const message = json.title
  const description = json.description
  if (!json.violations) {
    throw new ApiError(message, resp.status, description)
  }

  throw new ViolationsError(message, resp.status, description, json.violations)
}
